import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { AIChatPreviewCard } from "@/components/dashboard/AIChatPreviewCard";
import { finalizeIntake, intakeTurn } from "@/lib/api";

const speechLang = {
  en: "en-IN",
  hi: "hi-IN",
  bho: "hi-IN",
};

export const IntakeWorkspacePage = ({ session, language }) => {
  const navigate = useNavigate();
  const sessionId = sessionStorage.getItem("aavedak-intake-session-id") || "";
  const category = sessionStorage.getItem("aavedak-intake-category") || "General";
  const initialGreeting = sessionStorage.getItem("aavedak-intake-greeting") || "Welcome to Aavedak.";
  const channel = sessionStorage.getItem("aavedak-intake-channel") || "online";

  const [messages, setMessages] = useState([{ role: "agent", text: initialGreeting }]);
  const [inputText, setInputText] = useState("");
  const [confirmationNeeded, setConfirmationNeeded] = useState(false);
  const [canFinish, setCanFinish] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
    address: "",
    target_booth_id: channel === "booth" ? session.booth_id || "" : "",
    incident_location: "",
    incident_date: "",
    additional_notes: "",
  });

  const recognitionSupported = useMemo(
    () => Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
    [],
  );

  const speak = (text) => {
    if (!window.speechSynthesis || !text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = speechLang[language] || "hi-IN";
    utterance.rate = 0.95;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (!sessionId) {
      toast.error("Intake session missing. Start a new complaint.");
      navigate(session.role === "booth_incharge" ? "/booth/dashboard" : "/user/dashboard");
      return;
    }

    const timer = setTimeout(() => {
      speak(initialGreeting);
    }, 250);

    return () => clearTimeout(timer);
  }, [initialGreeting, navigate, session.role, sessionId]);

  const startVoiceCapture = () => {
    if (!recognitionSupported) {
      toast.error("Voice recognition not supported in this browser");
      return;
    }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.lang = speechLang[language] || "hi-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      setInputText(transcript);
    };
    recognition.onerror = () => {
      toast.error("Unable to capture voice");
    };
    recognition.start();
  };

  const sendTurn = async (preset) => {
    const message = (preset || inputText).trim();
    if (!message) {
      toast.error("Please provide your response");
      return;
    }
    setProcessing(true);
    try {
      const data = await intakeTurn(session.token, sessionId, { user_message: message });
      setMessages(data.turns);
      setCanFinish(Boolean(data.can_finish));
      setConfirmationNeeded(Boolean(data.confirmation_needed));
      setInputText("");
      if (data.follow_up_question) {
        speak(data.follow_up_question);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to process turn");
    } finally {
      setProcessing(false);
    }
  };

  const finalize = async () => {
    if (!canFinish) {
      toast.error("Please complete and confirm the full complaint narrative first.");
      return;
    }
    if (!form.full_name.trim() || !form.mobile_number.trim() || !form.incident_location.trim()) {
      toast.error("Please fill required applicant fields and incident location");
      return;
    }

    setProcessing(true);
    try {
      const complaint = await finalizeIntake(session.token, {
        session_id: sessionId,
        applicant_details: {
          full_name: form.full_name,
          mobile_number: form.mobile_number,
          email: form.email || null,
          address: form.address || null,
        },
        target_booth_id: form.target_booth_id || null,
        incident_location: form.incident_location,
        incident_date: form.incident_date || null,
        additional_notes: form.additional_notes || null,
      });
      toast.success(`Complaint created: ${complaint.complaint_id}`);
      sessionStorage.removeItem("aavedak-intake-session-id");
      sessionStorage.removeItem("aavedak-intake-category");
      sessionStorage.removeItem("aavedak-intake-channel");
      sessionStorage.removeItem("aavedak-intake-greeting");
      navigate(session.role === "booth_incharge" ? "/booth/dashboard" : "/user/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to finalize complaint");
    } finally {
      setProcessing(false);
    }
  };

  const selectedIncidentDate = form.incident_date ? new Date(form.incident_date) : undefined;
  const incidentDateLabel = form.incident_date || "Select incident date";

  return (
    <div className="intake-workspace" data-testid="intake-workspace-page">
      <div className="intake-header card">
        <div className="card-head">
          <h3>AI Intake: {category}</h3>
        </div>
        <div className="card-body">
          <p><strong>Session ID:</strong> {sessionId}</p>
          <p><strong>Channel:</strong> {channel}</p>
          <p><strong>Role:</strong> {session.role}</p>
        </div>
      </div>

      <div className="two-col" data-testid="intake-two-col-layout">
        <AIChatPreviewCard
          title="AI Conversational Intake"
          messages={messages}
          inputValue={inputText}
          onInputChange={setInputText}
          onSend={() => sendTurn()}
        />

        <div className="card" data-testid="intake-actions-card">
          <div className="card-head">
            <h3>Voice + Confirmation Controls</h3>
          </div>
          <div className="card-body form-grid">
            <Button variant="outline" onClick={startVoiceCapture} data-testid="start-voice-capture-btn">
              Capture Voice
            </Button>
            <Button onClick={() => sendTurn()} disabled={processing} data-testid="submit-intake-turn-btn">
              {processing ? "Processing..." : "Submit Turn"}
            </Button>

            {confirmationNeeded ? (
              <>
                <Button variant="outline" onClick={() => sendTurn("Yes, this is complete") } data-testid="confirm-intake-complete-btn">
                  Confirm Complete
                </Button>
                <Button variant="outline" onClick={() => sendTurn("No, I want to add more details") } data-testid="confirm-intake-more-btn">
                  Add More
                </Button>
              </>
            ) : null}
            <div className="status-chip" data-testid="intake-can-finish-chip">
              {canFinish ? "Ready to Finalize" : "Continue narration and confirmation"}
            </div>
          </div>
        </div>
      </div>

      <div className="card" data-testid="intake-finalize-form-card">
        <div className="card-head">
          <h3>Finalize Complaint Details</h3>
        </div>
        <div className="card-body form-grid">
          <Input value={form.full_name} onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))} placeholder="Applicant Full Name*" data-testid="finalize-fullname-input" />
          <Input value={form.mobile_number} onChange={(event) => setForm((prev) => ({ ...prev, mobile_number: event.target.value }))} placeholder="Applicant Mobile*" data-testid="finalize-mobile-input" />
          <Input value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} placeholder="Applicant Email" data-testid="finalize-email-input" />
          <Input value={form.address} onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))} placeholder="Applicant Address" data-testid="finalize-address-input" />
          <Input value={form.target_booth_id} onChange={(event) => setForm((prev) => ({ ...prev, target_booth_id: event.target.value }))} placeholder="Target Booth ID" data-testid="finalize-booth-input" />
          <Input value={form.incident_location} onChange={(event) => setForm((prev) => ({ ...prev, incident_location: event.target.value }))} placeholder="Incident Location*" data-testid="finalize-location-input" />
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="finalize-date-picker-trigger"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                <span data-testid="finalize-date-input">{incidentDateLabel}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid="finalize-date-picker-popover">
              <Calendar
                mode="single"
                selected={selectedIncidentDate}
                onSelect={(date) => {
                  const parsedDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
                  if (!parsedDate) {
                    return;
                  }
                  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
                  const day = String(parsedDate.getDate()).padStart(2, "0");
                  const isoDate = `${parsedDate.getFullYear()}-${month}-${day}`;
                  setForm((prev) => ({ ...prev, incident_date: isoDate }));
                  setDatePickerOpen(false);
                }}
                initialFocus
                data-testid="finalize-date-picker-calendar"
              />
            </PopoverContent>
          </Popover>
          <Textarea value={form.additional_notes} onChange={(event) => setForm((prev) => ({ ...prev, additional_notes: event.target.value }))} placeholder="Additional Notes" data-testid="finalize-notes-input" />

          <Button className="btn-sm btn-sm-orange" onClick={finalize} disabled={processing} data-testid="finalize-complaint-button">
            {processing ? "Finalizing..." : "Finalize Complaint"}
          </Button>
        </div>
      </div>
    </div>
  );
};
